import { Workflow, WorkflowSubmission } from '@aero-platform/shared'
import { RunStatus } from '@aero-platform/shared/generated/AeroStructures'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { FC, useMemo } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import SubmissionStatusCell from '../../components/submission/SubmissionStatusCell'
import { formatDateWithLocale } from '../../utils/intl'
import { currentUserState } from '../../utils/state-atoms'
import { computeSupersededIds } from '../../types/submission-util'

type CaseRow = {
  submissionName: string
  submissionId: string
  runId: string
  caseName: string
  systemRunId: string
  overallStatus: RunStatus
  workflow: Workflow[]
  isPrior: boolean
  timeCreated: number
  referenceRunName?: string
  description?: string
  submittedBy?: string
}

type ControlRow = {
  controlRow: true
  systemRunId: string
  rowCount: number
}

const isControlRow = (row: CaseRow | ControlRow): row is ControlRow => {
  return 'controlRow' in row
}

const deriveStatus = (workflow: any[]): RunStatus => {
  const statusPriority: Record<number, number> = {
    [RunStatus.ERROR]: 8,
    [RunStatus.RUNNING]: 7,
    [RunStatus.PENDING]: 6,
    [RunStatus.SUBMITTED]: 5,
    [RunStatus.WAITING]: 4,
    [RunStatus.UNSPECIFIED]: 3,
    [RunStatus.WARN_ON_ERROR]: 2,
    [RunStatus.COMPLETED]: 1,
    [RunStatus.CANCELLED]: 1,
    [RunStatus.CREATED]: 0
  }
  let highestPriority = -1
  let highestStatus = RunStatus.UNSPECIFIED

  workflow.forEach((wf: any) => {
    const st = wf.overallStatus ?? RunStatus.UNSPECIFIED
    const pri = statusPriority[st] ?? 0
    if (pri > highestPriority) {
      highestPriority = pri
      highestStatus = st
    }
  })

  return highestStatus === RunStatus.UNSPECIFIED ? RunStatus.CREATED : highestStatus
}

type Props = {
  submissions: WorkflowSubmission[]
  loading: boolean
  onOpenImages: (payload: {
    systemRunId: string
    runId: string
    caseName: string
    baselineRunId?: string
    ref2RunId?: string
  }) => void
}

const CaseView: FC<Props> = ({ submissions, loading, onOpenImages }) => {
  const currentUser = useRecoilValue(currentUserState)

  const rows = useMemo<CaseRow[]>(() => {
    const result: CaseRow[] = []
    submissions.forEach(submission => {
      // Compute superseding across ALL containers so a per-case resubmit (appended as a
      // separate container) retires the run it replaced. Keys drafting positions by the
      // following car's stable localPosition via getSupersedeCaseKey.
      const supersededIds = computeSupersededIds(
        (submission.submissionRunGroups || []).flatMap(c => c.workflowRunGroups)
      )
      submission.submissionRunGroups?.forEach(srg => {
        srg.workflowRunGroups?.forEach(wrg => {
          if (wrg.name === 'All') return
          const isPrior = supersededIds.has(wrg.systemRunId)
          result.push({
            submissionName: submission.name,
            submissionId: submission._id,
            runId: submission.name,
            caseName: wrg.name,
            systemRunId: wrg.systemRunId,
            overallStatus: deriveStatus(wrg.workflow || []),
            workflow: wrg.workflow || [],
            isPrior,
            timeCreated: submission.timeCreated,
            referenceRunName: submission.runConfig?.referenceRunName,
            description: submission.runConfig?.description,
            submittedBy: submission.submitUser
              ? `${submission.submitUser.firstName} ${submission.submitUser.lastName}`.trim()
              : ''
          })
        })
      })
    })
    // Filter out Prior cases - only show the latest version of each case,
    // then default to most recently run cases first.
    return result.filter(row => !row.isPrior).sort((a, b) => b.timeCreated - a.timeCreated)
  }, [submissions])

  const tableRows = useMemo<Array<CaseRow | ControlRow>>(() => {
    if (rows.length === 0) {
      return rows
    }

    return [{ controlRow: true, systemRunId: '__case-controls__', rowCount: rows.length }, ...rows]
  }, [rows])

  return (
    <>
      <DataTable
        value={tableRows}
        loading={loading}
        dataKey="systemRunId"
        rowClassName={(row: CaseRow | ControlRow) =>
          isControlRow(row) ? 'bg-gray-50 dark:bg-dark-blue-900' : ''
        }
        stripedRows
        paginator
        rows={100}
        rowsPerPageOptions={[50, 100, 200, 500]}
        pt={{
          root: { className: 'mt-8 no-expansion-space' },
          bodyRow: { className: 'table-row align-top' },
          paginator: {
            root: { className: 'justify-end' },
            RPPDropdown: { root: { className: 'max-w-[120px]' } }
          }
        }}>
        <Column
          header="Submission"
          field="submissionName"
          sortable
          className="w-[200px]"
          body={(row: CaseRow | ControlRow) => {
            if (isControlRow(row)) {
              return (
                <div className="flex items-center gap-4 control-row-content">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {row.rowCount} cases
                  </span>
                </div>
              )
            }
            return (
              <Link to={`/dashboard?caseId=${row.systemRunId}`} className="underline">
                {row.submissionName}
              </Link>
            )
          }}
        />
        <Column
          header="Run Description"
          field="description"
          className="w-[250px]"
          sortable
          body={(row: CaseRow | ControlRow) =>
            isControlRow(row) ? (
              <span className="control-row-content"></span>
            ) : (
              <span className="text-sm">{row.description}</span>
            )
          }
        />
        <Column
          header="Case"
          field="caseName"
          sortable
          body={(row: CaseRow | ControlRow) => {
            if (isControlRow(row)) {
              return <span className="control-row-content"></span>
            }

            return (
              <div className="flex items-center gap-2">
                <NavLink
                  to={`/submissions/${row.submissionName}?case=${row.systemRunId}`}
                  className="text-gm-blue dark:text-light-blue">
                  {row.caseName}
                </NavLink>
                {row.isPrior && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                    Prior
                  </span>
                )}
                {!row.isPrior && (
                  <button
                    title="View Post-Processing Images"
                    onClick={() =>
                      onOpenImages({
                        runId: row.runId,
                        systemRunId: row.systemRunId,
                        caseName: row.caseName,
                        baselineRunId: row.referenceRunName || undefined
                      })
                    }
                    className="p-1 rounded">
                    <i className="pi pi-images text-sm text-gray-600 dark:text-gray-400" />
                  </button>
                )}
              </div>
            )
          }}
        />
        <Column
          header="System Run ID"
          field="systemRunId"
          className="w-[200px]"
          sortable
          body={(row: CaseRow | ControlRow) =>
            isControlRow(row) ? (
              <span className="control-row-content"></span>
            ) : (
              <span className="text-sm">{row.systemRunId}</span>
            )
          }
        />
        <Column
          header="Created"
          field="timeCreated"
          className="w-[200px]"
          sortable
          body={(row: CaseRow | ControlRow) =>
            isControlRow(row) ? (
              <span className="control-row-content"></span>
            ) : (
              <span>{formatDateWithLocale(row.timeCreated, currentUser?.locale)}</span>
            )
          }
        />
        <Column
          header="Submitted By"
          field="submittedBy"
          className="w-[200px]"
          sortable
          body={(row: CaseRow | ControlRow) =>
            isControlRow(row) ? (
              <span className="control-row-content"></span>
            ) : (
              <span className="text-sm">{row.submittedBy}</span>
            )
          }
        />
        <Column
          header="Status"
          field="overallStatus"
          className="w-[200px]"
          sortable
          body={(row: CaseRow | ControlRow) =>
            isControlRow(row) ? (
              <span className="control-row-content"></span>
            ) : (
              <SubmissionStatusCell status={row.overallStatus} />
            )
          }
        />
      </DataTable>
    </>
  )
}

export default CaseView
